import { FC, ReactNode, createContext, useContext, useMemo } from 'react'
import { VisualAdminDTO, VisualCreativeDTO, VisualListResponse } from 'models/visuals'
import { VisualListResponseIsAdministratorResponse, VisualListResponseIsClientResponse, VisualListResponseIsCreativeResponse } from 'utils/typeguards'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'

import { Nullable } from 'models/helpers'
import { toBlurPartners } from '../blurPairs'
import { UseQueryResult } from '@tanstack/react-query'
import { useAuth0 } from 'utils/auth'
import { useGalleryVisualType } from './GalleryVisualType.context'
import { useListVisuals } from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'

interface GalleryVisualsMetaInterface {
  listVisualsQuery: UseQueryResult<VisualListResponse, any>
  allVisualsData: Nullable<VisualListResponse>
  allVisualsFilenames: string[]
  purchasedVisualsKeys: Set<string>
  unPurchasedVisualsKeys: Set<string>
  purchasedVisualsCount: number
  unPurchasedVisualsCount: number
  purchasedVisualsExist: boolean
  unPurchasedVisualsExist: boolean
  uploadMinCount: number
  uploadMaxCount: number
  preselectedFilenames: string[]
  selectedVisualsForStagingKeys: Set<string>
  selectedVisualsForStagingCount: number
  selectedVisualsForStagingExist: boolean
  visualsMetadataMap: Record<string, VisualAdminDTO | VisualCreativeDTO>
  prepurchasedVisualsTotal: number
  prepurchasedVisualsRemaining: number
  /** Each visual mapped to the other version of its photo (original <-> blurred), when both count as one photo */
  blurPartners: Map<string, string>
}

const defaultGalleryVisualsMetaValue: GalleryVisualsMetaInterface = {
  listVisualsQuery: {} as UseQueryResult<VisualListResponse, any>,
  allVisualsData: null,
  purchasedVisualsKeys: new Set(),
  unPurchasedVisualsKeys: new Set(),
  allVisualsFilenames: [],
  purchasedVisualsCount: 0,
  unPurchasedVisualsCount: 0,
  purchasedVisualsExist: false,
  unPurchasedVisualsExist: false,
  uploadMinCount: 0,
  uploadMaxCount: 0,
  preselectedFilenames: [],
  selectedVisualsForStagingKeys: new Set(),
  selectedVisualsForStagingCount: 0,
  selectedVisualsForStagingExist: false,
  visualsMetadataMap: {},
  prepurchasedVisualsTotal: 0,
  prepurchasedVisualsRemaining: 0,
  blurPartners: new Map(),
}

/** Gallery visuals meta context */
export const GalleryVisualsMetaContext = createContext<GalleryVisualsMetaInterface>(defaultGalleryVisualsMetaValue)
/** Gallery visuals meta context hook */
export const useGalleryVisualsMeta = (): GalleryVisualsMetaInterface => useContext(GalleryVisualsMetaContext)

/** Context provider for gallery visuals meta */
export const GalleryVisualsMetaContextProvider: FC<{
  assignmentId: string
  children?: ReactNode
}> = ({
  assignmentId,
  children,
}) => {
    const { normalizedOriginalType } = useGalleryVisualType()
    const { roles } = useAuth0()
    const { userPropertyWorkspaceId } = useUserData()

    const listVisualsQuery = useListVisuals(assignmentId, normalizedOriginalType, userPropertyWorkspaceId)
    const allVisualsData = useMemo(() => listVisualsQuery.data ?? null, [listVisualsQuery.data])

    const purchasedVisualsKeys = useMemo(() => new Set(
      allVisualsData && (VisualListResponseIsClientResponse(allVisualsData) || VisualListResponseIsAdministratorResponse(allVisualsData))
        ? [
          ...(allVisualsData.purchasedFilenames ?? []),
          ...(allVisualsData.selfEditedFilenames ?? [])
        ]
        : []
    ), [allVisualsData])

    const countPairsAsOne = useFeatureFlag(FeatureFlag.BLURRED_PAIRS_COUNT_AS_ONE)
    const blurPartners = useMemo(() => toBlurPartners(
      // Client galleries only: the admin gallery keeps counting files
      allVisualsData && VisualListResponseIsClientResponse(allVisualsData) ? allVisualsData.blurredPairs : null,
      countPairsAsOne
    ), [allVisualsData, countPairsAsOne])

    const unPurchasedVisualsKeys = useMemo(() => new Set(allVisualsData && VisualListResponseIsClientResponse(allVisualsData) ? Object.values(allVisualsData.visuals).filter(visual => !purchasedVisualsKeys.has(visual.name))
      .map(visual => visual.name) : []), [allVisualsData, purchasedVisualsKeys])
    const purchasedVisualsCount = useMemo(() => purchasedVisualsKeys.size, [purchasedVisualsKeys])
    const unPurchasedVisualsCount = useMemo(() => unPurchasedVisualsKeys.size, [unPurchasedVisualsKeys])
    const purchasedVisualsExist = useMemo(() => purchasedVisualsCount > 0, [purchasedVisualsCount])
    const unPurchasedVisualsExist = useMemo(() => unPurchasedVisualsKeys.size > 0, [unPurchasedVisualsKeys])
    const uploadMinCount = useMemo(() => {
      if (roles.isAdmin) return 1
      return allVisualsData && VisualListResponseIsCreativeResponse(allVisualsData) ? allVisualsData.minUploadCount : 0
    }, [allVisualsData, roles.isAdmin])
    const uploadMaxCount = useMemo(() => (allVisualsData && VisualListResponseIsCreativeResponse(allVisualsData) ? allVisualsData.maxUploadCount : 0), [allVisualsData])

    const preselectedFilenames = useMemo(() => (allVisualsData && (VisualListResponseIsCreativeResponse(allVisualsData) || VisualListResponseIsAdministratorResponse(allVisualsData)) ? allVisualsData.preselectedFilenames : []), [allVisualsData])
    const selectedVisualsForStagingKeys = useMemo(() => new Set(allVisualsData && VisualListResponseIsClientResponse(allVisualsData) ? Object.values(allVisualsData.purchasedFilenames) : []), [allVisualsData])
    const selectedVisualsForStagingCount = useMemo(() => selectedVisualsForStagingKeys.size, [selectedVisualsForStagingKeys])
    const selectedVisualsForStagingExist = useMemo(() => selectedVisualsForStagingCount > 0, [selectedVisualsForStagingCount])

    // Selection quantities
    const prepurchasedVisualsTotal = useMemo(() => {
      if (!allVisualsData) return 0
      if (VisualListResponseIsCreativeResponse(allVisualsData)) return 0
      else return allVisualsData.visualsQuantities?.prepurchasedTotalQuantity ?? 0
    }, [allVisualsData])

    const prepurchasedVisualsRemaining = useMemo(() => {
      if (!allVisualsData) return 0
      if (VisualListResponseIsCreativeResponse(allVisualsData)) return 0
      return allVisualsData.visualsQuantities?.prepurchasedRemainingQuantity ? Math.max(allVisualsData.visualsQuantities.prepurchasedRemainingQuantity, 0) : 0
    }, [allVisualsData])

    const allVisualsFilenames = useMemo(() => {
      if (!allVisualsData?.visuals) return []
      return allVisualsData.visuals.map(visual => visual.name)
    }, [allVisualsData])

    const visualsMetadataMap = useMemo(() => {
      const visuals = allVisualsData && (VisualListResponseIsCreativeResponse(allVisualsData) || VisualListResponseIsAdministratorResponse(allVisualsData)) ? allVisualsData.visuals : []

      return visuals.reduce((acc, item: VisualCreativeDTO | VisualAdminDTO) => {
        acc[item.name] = item
        return acc
      }, {} as Record<string, VisualCreativeDTO | VisualAdminDTO>)
    }, [allVisualsData])

    return (
      <GalleryVisualsMetaContext.Provider
        value={{
          listVisualsQuery,
          allVisualsData,
          allVisualsFilenames,
          purchasedVisualsKeys,
          purchasedVisualsCount,
          unPurchasedVisualsCount,
          purchasedVisualsExist,
          unPurchasedVisualsExist,
          unPurchasedVisualsKeys,
          uploadMinCount,
          uploadMaxCount,
          preselectedFilenames,
          prepurchasedVisualsTotal,
          prepurchasedVisualsRemaining,
          selectedVisualsForStagingKeys,
          selectedVisualsForStagingCount,
          selectedVisualsForStagingExist,
          visualsMetadataMap,
          blurPartners,
        }}
      >
        {children}
      </GalleryVisualsMetaContext.Provider>
    )
  }
